import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { testimonials, siteStats, faqData } from '@/mocks/anime';

export default function AboutPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于6080影视 - 动漫番剧免费高清在线观看平台',
    description: '6080影视是动漫番剧免费观看平台，提供高清不缓冲在线追番体验，海量免费动漫大全覆盖日漫、国漫、经典番剧。',
    url: 'https://6080ys.com/about',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://6080ys.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <header>
        <Navbar />
      </header>

      <main>
        <section className="pt-24 pb-8 bg-background-900/50">
          <div className="container-custom">
            <nav className="flex items-center gap-2 text-xs md:text-sm mb-6" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-500 hover:text-primary-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
              <span className="text-foreground-300">关于我们</span>
            </nav>

            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-50 mb-4">
                关于 <span className="text-gradient">6080影视</span>
              </h1>
              <p className="text-foreground-400 text-sm md:text-base leading-relaxed">
                致力于为全球动漫爱好者提供最优质的免费追番体验
              </p>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="container-custom">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {siteStats.map((stat) => (
                <div key={stat.label} className="text-center p-5 rounded-xl bg-background-800/50 border border-background-700/50">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400">
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-1">{stat.value}</div>
                  <div className="text-foreground-500 text-xs">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-900/50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 mb-3">品牌故事</h2>
                <div className="w-12 h-1 bg-primary-500 mx-auto rounded-full mb-4"></div>
              </div>
              <div className="space-y-5 text-foreground-400 text-sm md:text-base leading-relaxed">
                <p>
                  6080影视成立于2021年，由一群热爱动漫的年轻人创立。我们深知动漫迷对于高清画质和流畅播放的渴求，
                  因此从一开始就坚持以用户体验为核心，打造了一个免费、高清、无广告打扰的追番平台。
                </p>
                <p>
                  经过多年的发展，6080影视已经积累了超过<strong className="text-foreground-200">10,000部</strong>动漫资源，
                  涵盖日漫、国漫、经典番剧等多种类型。我们与多家内容提供商合作，确保番剧资源的丰富性和及时性，
                  新番通常在播出后<strong className="text-foreground-200">2小时内</strong>同步上线。
                </p>
                <p>
                  我们的技术团队持续优化播放体验，采用先进的CDN加速和多线服务器部署，
                  确保全国用户都能享受<strong className="text-foreground-200">不卡顿、不缓冲</strong>的流畅观看体验。
                  同时支持手机、平板、电脑多端访问，随时随地轻松追番。
                </p>
                <p>
                  6080影视承诺<strong className="text-primary-400">永久免费</strong>，
                  我们始终相信好的内容应该被更多人看到。未来，我们将继续扩大资源库，
                  优化产品体验，为动漫爱好者提供更好的服务。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="container-custom">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 mb-3">用户口碑</h2>
              <p className="text-foreground-500 text-sm">来自动漫迷的真实评价</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {testimonials.map((t, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-background-800/50 border border-background-700/50">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-300 text-sm leading-relaxed mb-4">{t.comment}</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-9 h-9 rounded-full object-cover"
                    />
                    <span className="text-foreground-200 text-sm font-medium">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-900/50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 mb-3">联系我们</h2>
                <p className="text-foreground-500 text-sm">有任何问题或建议，欢迎随时联系我们</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="p-5 rounded-xl bg-background-800/50 border border-background-700/50 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-400">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h4 className="text-foreground-200 font-medium text-sm mb-1">电子邮箱</h4>
                  <p className="text-foreground-500 text-xs">contact@6080ys.com</p>
                </div>
                <div className="p-5 rounded-xl bg-background-800/50 border border-background-700/50 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-400">
                    <i className="ri-customer-service-line text-xl"></i>
                  </div>
                  <h4 className="text-foreground-200 font-medium text-sm mb-1">在线客服</h4>
                  <p className="text-foreground-500 text-xs">工作日 9:00-18:00</p>
                </div>
                <div className="p-5 rounded-xl bg-background-800/50 border border-background-700/50 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-400">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <h4 className="text-foreground-200 font-medium text-sm mb-1">公司地址</h4>
                  <p className="text-foreground-500 text-xs">中国 · 深圳</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="container-custom">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 mb-3">常见问题</h2>
              <p className="text-foreground-500 text-sm">关于6080影视，你想知道的都在这里</p>
            </div>

            <div className="max-w-3xl mx-auto space-y-3">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className="rounded-xl bg-background-800/50 border border-background-700/50 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="text-foreground-100 text-sm md:text-base font-medium pr-4">
                      {faq.question}
                    </span>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full transition-all duration-300 flex-shrink-0 ${
                      openFaqIndex === index
                        ? 'bg-primary-500 text-white rotate-45'
                        : 'bg-background-700 text-foreground-400'
                    }`}>
                      <i className="ri-add-line text-sm"></i>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      openFaqIndex === index ? 'max-h-96 pb-4 px-5' : 'max-h-0'
                    }`}
                  >
                    <p className="text-foreground-400 text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />
    </>
  );
}